import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

const REQUEST_TIMEOUT_MS = 30_000;

/**
 * URL fragments that should bypass the interceptor entirely (no token attached, no 401 handling).
 * Include the Refresh endpoint to avoid infinite refresh loops.
 */
const PUBLIC_URL_FRAGMENTS = ['/assets/', '/login', '/Refresh'];

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Functional HTTP interceptor — Angular standalone pattern.
 * Attaches Bearer token, handles 401 refresh with queuing, 30s timeout.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip token attachment and error handling for public/auth endpoints
  if (PUBLIC_URL_FRAGMENTS.some(fragment => req.url.includes(fragment))) {
    return next(req);
  }

  // inject() must be called at the top level of the interceptor function
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const router = inject(Router);

  const token = authService.getAccessToken();
  if (token) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(req).pipe(
    timeout(REQUEST_TIMEOUT_MS),
    catchError((error: HttpErrorResponse | Error) => {
      if (error.name === 'TimeoutError') {
        toastService.error('Request timed out. Please try again.');
        return throwError(() => error);
      }

      if (error instanceof HttpErrorResponse) {
        switch (error.status) {
          case 401:
            return handle401(req, next, authService, toastService, router);

          case 400:
          case 422: {
            // Validation errors — extract field messages or detail
            const { title, detail } = extractClientErrorMessage(error);
            toastService.error(title, detail);
            break;
          }

          case 403: {
            const msg = error.error?.detail || 'Access denied. You do not have permission to perform this action.';
            toastService.error('Forbidden', msg);
            break;
          }

          case 404: {
            const msg = error.error?.detail || 'The requested resource was not found.';
            toastService.error('Not Found', msg);
            break;
          }

          case 409: {
            const msg = error.error?.detail || 'A conflict occurred. Please refresh and try again.';
            toastService.error('Conflict', msg);
            break;
          }

          case 429:
            toastService.error('Too Many Requests', 'Too many requests. Please try again later.');
            break;

          case 502:
          case 503:
            toastService.error('Service Unavailable', 'Service temporarily unavailable. Please try again later.');
            break;

          case 504:
            toastService.error('Timeout', 'Request timed out. Please try again.');
            break;

          default:
            if (error.status >= 400 && error.status < 500) {
              const { title, detail } = extractClientErrorMessage(error);
              toastService.error(title, detail);
            } else if (error.status >= 500) {
              toastService.error('A server error occurred. Please try again later.');
            }
            break;
        }
      }

      return throwError(() => error);
    })
  );
};

function handle401(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  authService: AuthService,
  toastService: ToastService,
  router: Router
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshAndStore().pipe(
      switchMap(() => {
        isRefreshing = false;
        const token = authService.getAccessToken();
        refreshTokenSubject.next(token);

        return next(
          req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          })
        );
      }),
      catchError(err => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        doRedirectToLogin(authService, toastService, router);
        return throwError(() => err);
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter((t): t is string => t !== null),
    take(1),
    switchMap(t =>
      next(
        req.clone({
          setHeaders: {
            Authorization: `Bearer ${t}`
          }
        })
      )
    )
  );
}

function doRedirectToLogin(authService: AuthService, toastService: ToastService, router: Router): void {
  authService.clearAuthData();
  toastService.error('Your session has expired. Please log in again.');
  router.navigate(['/login']);
}

/**
 * Extract a user-facing message from a client error (4xx) response.
 * The API is expected to return meaningful messages for client errors
 * (validation failures, not found, forbidden, etc.).
 */
function extractClientErrorMessage(error: HttpErrorResponse): { title: string; detail: string } {
  const body = error.error;

  // Validation errors array: [{ field, message }]
  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    const messages = body.errors.map((e: { message?: string }) => e.message).filter(Boolean);
    if (messages.length > 0) {
      return { title: body.title || 'Validation Error', detail: messages.join(' ') };
    }
  }

  // RFC 7807 detail field
  if (typeof body?.detail === 'string' && body.detail.trim()) {
    return { title: body.title || 'Error', detail: body.detail };
  }

  // Standard message field
  if (typeof body?.message === 'string' && body.message.trim()) {
    return { title: body.title || 'Error', detail: body.message };
  }

  // At least a title from the API
  if (typeof body?.title === 'string' && body.title.trim()) {
    return { title: body.title, detail: '' };
  }

  // Plain string body
  if (typeof body === 'string' && body.trim()) {
    return { title: 'Error', detail: body };
  }

  // Nothing usable in the body — single generic fallback
  return { title: 'Request Failed', detail: `The server returned an error (${error.status}). Please try again.` };
}
